import { useState, useEffect } from 'react';
import { Avatar as MuiAvatar, AvatarProps, Skeleton } from '@mui/material';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'https://stardust-aims.amy.care';

interface AuthenticatedAvatarProps extends Omit<AvatarProps, 'src'> {
  src?: string;
  fallback?: string;
}

/**
 * Avatar component that handles JWT-authenticated image loading
 * Converts API URLs to blob URLs with proper authentication
 */
export default function AuthenticatedAvatar({ src, fallback, children, ...props }: AuthenticatedAvatarProps) {
  const [imageSrc, setImageSrc] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    // If no src or src doesn't start with /api (not authenticated endpoint), use directly
    if (!src || !src.startsWith('/api')) {
      setImageSrc(src);
      return;
    }

    // Load image with authentication
    const loadAuthenticatedImage = async () => {
      setLoading(true);
      setError(false);

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError(true);
          setLoading(false);
          return;
        }

        // Use full URL to ensure it goes to API domain, not admin domain
        const fullUrl = src.startsWith('http') ? src : `${API_URL}${src}`;
        
        const response = await axios.get(fullUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: 'blob',
        });

        // Create blob URL
        const blobUrl = URL.createObjectURL(response.data);
        setImageSrc(blobUrl);
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadAuthenticatedImage();

    // Cleanup blob URL on unmount
    return () => {
      if (imageSrc && imageSrc.startsWith('blob:')) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [src]);

  // Show loading skeleton
  if (loading && src) {
    return <Skeleton variant="circular" sx={props.sx} />;
  }

  // Show avatar with error fallback or blob URL
  // Only pass src to Avatar if we have a blob URL or fallback, never the original path
  return (
    <MuiAvatar
      {...props}
      src={error ? fallback : (imageSrc || undefined)}
    >
      {children}
    </MuiAvatar>
  );
}
